<template>
  <div class="contact-page">
    <section class="page-hero">
      <div class="container grid-two contact-hero">
        <div class="contact-hero__main">
          <span class="eyebrow">{{ content.eyebrow }}</span>
          <h1 class="contact-hero__title">{{ content.title }}</h1>
        </div>
        <div class="contact-side card-panel">
          <strong>{{ content.companyLabel }}</strong>
          <span>{{ content.companyName }}</span>
          <strong>{{ content.telLabel }}</strong>
          <a href="tel:+81368694765">+81 3-6869-4765</a>
          <strong>{{ content.faxLabel }}</strong>
          <span>+81 3-6869-4769</span>
          <strong>{{ content.mailLabel }}</strong>
          <a href="mailto:tokyo02@jbc-japan.co.jp">tokyo02@jbc-japan.co.jp</a>
        </div>
      </div>
    </section>

    <section class="section-tight">
      <div class="container">
        <form class="contact-form card-panel" @submit.prevent="submitForm">
          <div class="contact-form__grid">
            <label>
              <span>{{ content.form.nameLabel }}</span>
              <input v-model="form.name" type="text" :placeholder="content.form.namePlaceholder">
              <small v-if="errors.name">{{ errors.name }}</small>
            </label>
            <label>
              <span>{{ content.form.emailLabel }}</span>
              <input v-model="form.email" type="email" :placeholder="content.form.emailPlaceholder">
              <small v-if="errors.email">{{ errors.email }}</small>
            </label>
          </div>
          <label>
            <span>{{ content.form.companyLabel }}</span>
            <input v-model="form.company" type="text" :placeholder="content.form.companyPlaceholder">
          </label>
          <label>
            <span>{{ content.form.messageLabel }}</span>
            <textarea v-model="form.message" rows="6" :placeholder="content.form.messagePlaceholder"></textarea>
            <small v-if="errors.message">{{ errors.message }}</small>
          </label>
          <div class="contact-form__actions">
            <button class="btn" type="submit" :disabled="submitting">{{ submitting ? content.form.submitPending : content.form.submitIdle }}</button>
            <p v-if="statusMessage" class="contact-form__status">{{ statusMessage }}</p>
          </div>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
import { validateContactForm } from '../utils/contact';

var contactContent = {
  en: {
    eyebrow: 'CONTACT',
    title: 'Tell us about your destination, audience, and timeline.',
    companyLabel: 'Company',
    companyName: 'JBC TOURS',
    telLabel: 'TEL',
    faxLabel: 'FAX',
    mailLabel: 'Mail',
    form: {
      nameLabel: 'Name',
      namePlaceholder: 'Your name',
      emailLabel: 'Email',
      emailPlaceholder: 'you@example.com',
      companyLabel: 'Company',
      companyPlaceholder: 'Company or brand name',
      messageLabel: 'Message',
      messagePlaceholder: 'Share the project background, destination, and preferred timeline.',
      submitIdle: 'Send Inquiry',
      submitPending: 'Sending...'
    },
    status: {
      error: 'Unable to submit your inquiry right now.',
      success: 'Inquiry submitted successfully.'
    }
  },
  ja: {
    eyebrow: 'CONTACTお問い合わせ',
    title: 'ご旅行の目的・対象者・ご希望時期についてお聞かせください。',
    companyLabel: '会社名',
    companyName: 'JBC TOURS',
    telLabel: 'TEL',
    faxLabel: 'FAX',
    mailLabel: 'メールアドレス',
    form: {
      nameLabel: 'お名前',
      namePlaceholder: 'お名前をご入力ください',
      emailLabel: 'メールアドレス',
      emailPlaceholder: 'you@example.com',
      companyLabel: '会社名',
      companyPlaceholder: '会社名またはブランド名をご入力ください',
      messageLabel: 'お問い合わせ内容',
      messagePlaceholder: 'ご相談内容、ご希望の目的地、時期などをご記入ください。',
      submitIdle: 'お問い合わせを送信',
      submitPending: '送信中...'
    },
    status: {
      error: '現在お問い合わせを送信できません。',
      success: 'お問い合わせを送信しました。'
    }
  }
};

export default {
  name: 'Contact',
  props: {
    language: {
      type: String,
      default: 'en'
    }
  },
  data: function () {
    return {
      form: {
        name: '',
        email: '',
        company: '',
        message: ''
      },
      errors: {},
      submitting: false,
      statusMessage: ''
    };
  },
  computed: {
    content: function () {
      return contactContent[this.language] || contactContent.en;
    }
  },
  methods: {
    submitForm: async function () {
      var errors = validateContactForm(this.form, this.language);
      this.errors = errors;
      this.statusMessage = '';

      if (Object.keys(errors).length) {
        return;
      }

      this.submitting = true;

      try {
        var response = await fetch('http://localhost:8081/api/inquiries', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.form)
        });
        var result = await response.json();

        if (!response.ok) {
          this.statusMessage = result.message || this.content.status.error;
          return;
        }

        this.statusMessage = result.message || this.content.status.success;
        this.form = {
          name: '',
          email: '',
          company: '',
          message: ''
        };
      } catch (error) {
        this.statusMessage = this.content.status.error;
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.contact-hero {
  align-items: start;
}

.contact-hero__main {
  display: grid;
  gap: 10px;
  align-content: start;
  max-width: 520px;
}

.contact-hero__title {
  margin: 0;
  font-size: clamp(24px, 3vw, 32px);
  line-height: 1.35;
}

.contact-hero__meta {
  margin: 0;
  color: var(--color-muted);
  line-height: 1.8;
}

.contact-side,
.contact-form {
  padding: 36px;
}

.contact-side strong,
.contact-side a,
.contact-side span {
  display: block;
}

.contact-side strong {
  margin-bottom: 8px;
}

.contact-side a,
.contact-side span {
  margin-bottom: 18px;
  color: var(--color-muted);
}

.contact-form {
  display: grid;
  gap: 22px;
}

.contact-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;
}

label {
  display: grid;
  gap: 10px;
}

label span {
  font-size: 13px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

input,
textarea {
  width: 100%;
  border: 1px solid rgba(109, 86, 68, 0.16);
  border-radius: 18px;
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.72);
}

small {
  color: #b04f3f;
}

.contact-form__actions {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.contact-form__status {
  margin: 0;
  color: var(--color-muted);
}

@media (max-width: 767px) {
  .contact-hero__title {
    font-size: 24px;
  }

  .contact-form__grid {
    grid-template-columns: 1fr;
  }
}
</style>
